/**
 * Shared SEO head helper for static and content pages.
 *
 * Renders the standard meta block (title, description, robots, og, twitter,
 * canonical, hreflang, x-default) so every page emits consistent, complete
 * SEO signals — including in SSR, where `window` is undefined and any
 * `window.location.origin` fallback would silently drop these tags from the
 * crawler-visible HTML.
 */
import type { ReactNode } from "react";
import { SUPPORTED_LANGS as LANGS, OG_LOCALE, langSegmentRegex, type Lang } from "@majourneys/shared";

export const SITE_ORIGIN = "https://majourneys.com";

/** 1200x630 brand card used when a page has no photo of its own. Without
 *  it, WhatsApp and iMessage blow the logo up into a blurry crop. */
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/images/og/majourneys-share.jpg`;
export const SUPPORTED_LANGS = LANGS;
export type SupportedLang = Lang;

export function ogLocaleFor(lang: string): string {
  return OG_LOCALE[lang as Lang] ?? OG_LOCALE.en;
}

/**
 * Swap the `/<lang>/` segment in `path` for each supported locale to build
 * the hreflang alternates. Path should start with `/<lang>/...` (the typical
 * form returned by canonical builders).
 */
export function alternateFor(path: string, target: SupportedLang): string {
  return path.replace(langSegmentRegex(), `/${target}`);
}

export function hreflangEntries(
  canonicalPath: string,
  langs: readonly SupportedLang[],
  alternates?: Partial<Record<SupportedLang, string>>,
): Array<{ hrefLang: string; href: string }> {
  const href = (l: SupportedLang) =>
    `${SITE_ORIGIN}${alternates?.[l] ?? alternateFor(canonicalPath, l)}`;
  return [
    ...langs.map((l) => ({ hrefLang: l, href: href(l) })),
    { hrefLang: "x-default", href: href("en") },
  ];
}

/**
 * "{title} | MaJourneys", unless the title already names the brand (some
 * locale titles carry it, e.g. discover.seo_title), which used to give
 * "... | MaJourneys | MaJourneys" on the Liked page.
 */
export function withBrand(title: string): string {
  return /majourneys/i.test(title) ? title : `${title} | MaJourneys`;
}

export interface PageSeoProps {
  /** Document title — leaf-most + brand suffix is the convention. */
  title: string;
  /** Meta description (≤160 chars; we do not shorten it for you). */
  description: string;
  /** Canonical path starting with `/<lang>/...`, e.g. `/en/blog`. */
  canonicalPath: string;
  /** Active locale; drives og:locale and hreflang. */
  lang: string;
  /** og:type — "website" for lists, "article" for articles, "product" for items. */
  ogType?: "website" | "article" | "product" | "profile";
  /** Optional og:image URL (absolute). */
  ogImage?: string;
  /** Optional comma-joinable keyword list for legacy `<meta name=keywords>`. */
  keywords?: string[] | string;
  /** Robots directive override. Default = index,follow,max-image-preview:large. */
  robots?: string;
  /**
   * Optional alternate-locale paths. When omitted, alternates are derived
   * from `canonicalPath` by swapping the `/<lang>/` prefix. Provide overrides
   * only when locale paths diverge structurally (e.g. blog slug differs
   * across languages).
   */
  alternates?: Partial<Record<SupportedLang, string>>;
  /** Restrict hreflang alternates to these locales (default: all supported).
   *  Use when only some locales have real content for this URL. */
  hreflangLangs?: SupportedLang[];
  /** Optional extra JSON-LD documents to emit alongside the standard block. */
  jsonLd?: Array<Record<string, unknown>>;
  /** Extra head children (e.g. preload links, additional og tags). */
  children?: ReactNode;
}

export function PageSeo(props: PageSeoProps) {
  const {
    title,
    description,
    canonicalPath,
    lang,
    ogType = "website",
    ogImage = DEFAULT_OG_IMAGE,
    keywords,
    robots = "index,follow,max-image-preview:large,max-snippet:-1",
    alternates,
    hreflangLangs = SUPPORTED_LANGS,
    jsonLd,
    children,
  } = props;
  const canonical = `${SITE_ORIGIN}${canonicalPath}`;
  const keywordsStr = Array.isArray(keywords) ? keywords.join(", ") : keywords;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywordsStr && <meta name="keywords" content={keywordsStr} />}
      <meta name="robots" content={robots} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="MaJourneys" />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={ogLocaleFor(lang)} />
      {hreflangLangs.filter((l) => l !== lang).map((l) => (
        <meta key={l} property="og:locale:alternate" content={ogLocaleFor(l)} />
      ))}
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <link rel="canonical" href={canonical} />
      {hreflangEntries(canonicalPath, hreflangLangs, alternates).map((e) => (
        <link key={e.hrefLang} rel="alternate" hrefLang={e.hrefLang} href={e.href} />
      ))}
      {jsonLd?.map((doc, i) => (
        <script
          key={`ld-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(doc) }}
        />
      ))}
      {children}
    </>
  );
}
